import { useState, useEffect } from 'react';
import {
  AlertTriangle,
  Sparkles,
  Save,
  ArrowRight,
  Shield,
  Car,
  Calendar,
  Phone,
  User,
  Hash,
} from 'lucide-react';
import Dialog from '@components/ui/Dialog.js';
import Button from '@components/ui/Button.js';
import Input from '@components/ui/Input.js';
import { cn } from '@utils/Cn.js';
import type { ImportPreviewRow } from '../types/index.js';

interface EditRowModalProps {
  row: ImportPreviewRow | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (rowNumber: number, overrides: Record<string, unknown>) => void;
  onSaveAndNext?: (rowNumber: number, overrides: Record<string, unknown>) => void;
  hasNextIssue?: boolean;
}

const COMMON_POLICY_TYPES = [
  'Motor',
  'Health',
  'Group Insurance',
  'Workmen Compensation',
  'Fire',
  'Burglary',
  'Life',
  'Commercial Vehicle',
];

export default function EditRowModal({
  row,
  isOpen,
  onClose,
  onSave,
  onSaveAndNext,
  hasNextIssue = false,
}: EditRowModalProps) {
  const [clientName, setClientName] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [policyTypeName, setPolicyTypeName] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [endDate, setEndDate] = useState('');
  const [referenceName, setReferenceName] = useState('');
  const [referenceNote, setReferenceNote] = useState('');

  useEffect(() => {
    if (row) {
      setClientName(row.clientName);
      setMobileNumber(row.mobileNumber ?? '');
      setPolicyTypeName(row.policyTypeName);
      setVehicleNumber(row.vehicleNumber ?? '');
      setPolicyNumber(row.policyNumber ?? '');
      setEndDate(row.endDate);
      setReferenceName(row.referenceName ?? '');
      setReferenceNote(row.referenceNote ?? '');
    }
  }, [row]);

  if (!row) return null;

  const isFailed = row.status === 'FAILED';

  // Smart fix: check if the vehicle number was mistakenly filled with a policy type like GROUP or WC
  const vehicleUpper = (vehicleNumber || '').toUpperCase().trim();
  const isVehicleActuallyGroup = vehicleUpper === 'GROUP' || vehicleUpper.includes('GROUP');
  const isVehicleActuallyAuto = vehicleUpper === 'AUTO';

  function handleApplyGroupFix() {
    setPolicyTypeName('Group Insurance');
    setVehicleNumber('');
  }

  function handleApplyAutoFix() {
    setPolicyTypeName('Motor');
    setVehicleNumber('');
  }

  function handleClearVehicle() {
    setVehicleNumber('');
  }

  function getOverridesPayload(): Record<string, unknown> {
    return {
      insuranceHolderName: clientName.trim(),
      mobileNumber: mobileNumber.trim() || null,
      policyTypeName: policyTypeName.trim() || 'Motor',
      vehicleNumber: vehicleNumber.trim() ? vehicleNumber.trim().toUpperCase() : null,
      policyNumber: policyNumber.trim() || null,
      endDate: endDate.trim(),
      referenceName: referenceName.trim() || null,
      referenceNote: referenceNote.trim() || null,
    };
  }

  function handleSaveClick() {
    if (!row) return;
    onSave(row.rowNumber, getOverridesPayload());
    onClose();
  }

  function handleSaveAndNextClick() {
    if (!row) return;
    if (onSaveAndNext) {
      onSaveAndNext(row.rowNumber, getOverridesPayload());
    } else {
      handleSaveClick();
    }
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title={`Edit Record — Row #${String(row.rowNumber)}`}
      description="Quickly correct fields for this record. All changes update instantly."
      className="sm:max-w-xl max-h-[90vh] overflow-y-auto"
      sheet
    >
      <div className="space-y-5 py-2">
        {/* Issue Warning Banner with 1-Click Fix */}
        {isFailed && row.reason && (
          <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-3.5 space-y-2.5">
            <div className="flex items-start gap-2.5">
              <AlertTriangle size={18} className="text-red-600 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-xs font-bold text-red-700 dark:text-red-300">
                  Issue Detected
                </p>
                <p className="text-xs text-red-600 dark:text-red-400 mt-0.5 leading-relaxed">
                  {row.reason}
                </p>
              </div>
            </div>

            {/* Smart 1-Click Action Buttons */}
            {isVehicleActuallyGroup && (
              <div className="pt-1 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleApplyGroupFix}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-amber-500 text-white hover:bg-amber-600 transition-colors shadow-2xs cursor-pointer"
                >
                  <Sparkles size={13} />
                  1-Click Fix: Set Policy Type to "Group Insurance" &amp; Clear Vehicle
                </button>
              </div>
            )}

            {isVehicleActuallyAuto && (
              <div className="pt-1 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleApplyAutoFix}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-amber-500 text-white hover:bg-amber-600 transition-colors shadow-2xs cursor-pointer"
                >
                  <Sparkles size={13} />
                  1-Click Fix: Set as "Motor" &amp; Clear Invalid Vehicle Tag
                </button>
              </div>
            )}
          </div>
        )}

        {/* Clean Form Inputs */}
        <div className="space-y-4">
          {/* Insured Client Name */}
          <div>
            <label className="text-xs font-bold text-ink block mb-1 flex items-center gap-1.5">
              <User size={13} className="text-slate" />
              Insured / Client Name <span className="text-red-500">*</span>
            </label>
            <Input
              value={clientName}
              onChange={(e) => { setClientName(e.target.value); }}
              placeholder="e.g. Ramesh Kumar"
              className="text-xs font-semibold"
            />
          </div>

          {/* Mobile Number */}
          <div>
            <label className="text-xs font-bold text-ink block mb-1 flex items-center gap-1.5">
              <Phone size={13} className="text-slate" />
              Mobile Number <span className="text-red-500">*</span>
            </label>
            <Input
              value={mobileNumber}
              onChange={(e) => { setMobileNumber(e.target.value); }}
              placeholder="10-digit Indian mobile (e.g. 9842100000)"
              className="text-xs font-mono"
            />
          </div>

          {/* Policy Type with quick select chips */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-ink block flex items-center gap-1.5">
              <Shield size={13} className="text-slate" />
              Policy Type <span className="text-red-500">*</span>
            </label>
            <Input
              value={policyTypeName}
              onChange={(e) => { setPolicyTypeName(e.target.value); }}
              placeholder="e.g. Motor or Health"
              className="text-xs font-semibold"
            />
            {/* Quick Chips for Policy Types */}
            <div className="flex flex-wrap gap-1.5 pt-1">
              {COMMON_POLICY_TYPES.map((pt) => (
                <button
                  key={pt}
                  type="button"
                  onClick={() => { setPolicyTypeName(pt); }}
                  className={cn(
                    'px-2 py-0.5 rounded-lg text-[10px] font-bold border transition-colors cursor-pointer',
                    policyTypeName.toLowerCase() === pt.toLowerCase()
                      ? 'bg-slate text-white border-slate'
                      : 'bg-paper text-ink-faint border-line hover:border-slate/50',
                  )}
                >
                  {pt}
                </button>
              ))}
            </div>
          </div>

          {/* Vehicle Number & Expiry Date Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-bold text-ink flex items-center gap-1.5">
                  <Car size={13} className="text-slate" />
                  Vehicle Number
                </label>
                {vehicleNumber && (
                  <button
                    type="button"
                    onClick={handleClearVehicle}
                    className="text-[10px] text-amber-600 hover:underline cursor-pointer"
                  >
                    Clear (Non-Motor)
                  </button>
                )}
              </div>
              <Input
                value={vehicleNumber}
                onChange={(e) => { setVehicleNumber(e.target.value); }}
                placeholder="e.g. TN33AR0009 or leave empty"
                className="text-xs font-mono uppercase"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-ink block mb-1 flex items-center gap-1.5">
                <Calendar size={13} className="text-slate" />
                Expiry Date <span className="text-red-500">*</span>
              </label>
              <Input
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); }}
                placeholder="DD/MM/YYYY"
                className="text-xs font-mono"
              />
            </div>
          </div>

          {/* Policy Certificate Number */}
          <div>
            <label className="text-xs font-bold text-ink block mb-1 flex items-center gap-1.5">
              <Hash size={13} className="text-slate" />
              Policy Number
            </label>
            <Input
              value={policyNumber}
              onChange={(e) => { setPolicyNumber(e.target.value); }}
              placeholder="e.g. 1706003125P108... or leave blank"
              className="text-xs font-mono"
            />
          </div>

          {/* Reference Name & Notes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-ink block mb-1">
                Reference Name
              </label>
              <Input
                value={referenceName}
                onChange={(e) => { setReferenceName(e.target.value); }}
                placeholder="e.g. Pradeep Gym"
                className="text-xs"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-ink block mb-1">
                Remarks / Notes
              </label>
              <Input
                value={referenceNote}
                onChange={(e) => { setReferenceNote(e.target.value); }}
                placeholder="e.g. 3 Years Policy"
                className="text-xs"
              />
            </div>
          </div>
        </div>

        {/* Modal Actions */}
        <div className="flex flex-col-reverse sm:flex-row sm:items-center justify-end gap-2 pt-4 border-t border-line/60">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>

          {hasNextIssue && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleSaveAndNextClick}
              rightIcon={<ArrowRight size={14} />}
              className="w-full sm:w-auto font-bold"
            >
              Save &amp; Next Issue →
            </Button>
          )}

          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleSaveClick}
            leftIcon={<Save size={14} />}
            className="w-full sm:w-auto font-bold"
          >
            Save Changes
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
